import {put, call, all} from 'redux-saga/effects';
import Milestone from "../../entities/milestone";
import * as Actions from "../actions/milestoneActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type MilestoneAction = {
    milestoneId: string;
    milestone: Milestone;
    milestones: Milestone[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneMilestone(action: MilestoneAction) {
	const session = tabSession.captureGeneration();
	try {
		const milestone: Milestone = yield call(api.findOne, 'milestone', action.milestoneId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.MILESTONE_FETCH_ONE_SUCCESS, milestoneId: action.milestoneId, milestone, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.MILESTONE_FETCH_ONE_FAILURE, milestoneId: action.milestoneId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {MilestoneAction} action
 */
export function* saveMilestone(action: MilestoneAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ MILESTONE_SAVE saga', action.milestone?.id);

		if (action.milestone === undefined || action.milestone === null) {
			console.error('saveMilestone received a null');
			throw new Error('saveMilestone received a null');
		}

		if (!(action.milestone instanceof Milestone)) {
			action.milestone = new Milestone(action.milestone);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.milestone.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.milestone);
			const addResult = yield call(idb.add, 'milestone', action.milestone);
			if (addResult.isErr()) {
				console.error(`⮂ saveMilestone: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertMilestoneSave(action.milestone, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.MILESTONE_SAVE_FAILURE, milestone: action.milestone, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.MILESTONE_SAVE, milestone: action.milestone, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertMilestoneSave(action.milestone, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.MILESTONE_SAVE_FAILURE, milestone: action.milestone, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {MilestoneAction} action
 * @param {Milestone[]} action.milestones
 */
export function* saveManyMilestones(action: MilestoneAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ MILESTONE_SAVE_MANY saga', action.milestones.map(milestone => milestone.id).join(','));

        if (!Array.isArray(action.milestones)) {
            console.error('saveManyMilestones expected an array, received', action.milestones);
            throw new Error('saveManyMilestones expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveMilestone), under these queue keys
		const dispatchedAddedOns: number[] = action.milestones.map(milestone => milestone?.addedOn);
		const revertAll = () => action.milestones.map((milestone, i) =>
			put(Actions.revertMilestoneSave(milestone, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Milestone[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.milestones.length; i++) {
				const milestone = action.milestones[i];
				milestone.persisted = false;
				ensureOperationId(milestone);
				toSave.push(milestone);
			}

			const addResult = yield call(idb.add, 'milestone', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyMilestones: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.MILESTONE_SAVE_MANY_FAILURE, milestones: action.milestones, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.MILESTONE_SAVE_MANY, milestones: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.MILESTONE_SAVE_MANY_FAILURE, milestones: action.milestones, status: 500, err});

			console.error(`Error saving ${action.milestones.length} milestones to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {MilestoneAction} action
 */
export function* removeMilestone(action: MilestoneAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.milestone === undefined || action.milestone === null || !(action.milestone instanceof Milestone)) {
			console.error('removeMilestone expected type Milestone, received', action.milestone);
			reportQueued(false);
			throw new Error('removeMilestone expected type Milestone');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.milestone.status = 'D';
		action.milestone.addedOn = newHRTimestamp();
		action.milestone.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.milestone);
		const addResult = yield call(idb.add, 'milestone', action.milestone);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeMilestone: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.MILESTONE_REMOVE_FAILURE, milestone: action.milestone, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.MILESTONE_REMOVE, milestone: action.milestone});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.MILESTONE_REMOVE_FAILURE, milestone: action.milestone, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Milestone.
	*
	* @param {MilestoneAction} action
	*/
export function* restoreMilestone(action: MilestoneAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreMilestone expected getCurrentUserId() to return a value');
        return;
    }

	if (action.milestone === undefined || action.milestone === null) {
        console.error('restoreMilestone expected a Milestone, received null');
        throw new Error('restoreMilestone expected a Milestone, received null');
    }
    else if (!(action.milestone instanceof Milestone)) action.milestone = new Milestone(action.milestone); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.milestone.status = 'U';
		action.milestone.addedOn = newHRTimestamp();
		action.milestone.addedBy = userId;
		action.milestone.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.milestone);
		const addResult = yield call(idb.add, 'milestone', action.milestone);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreMilestone: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.MILESTONE_RESTORE_FAILURE, milestone: action.milestone, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.MILESTONE_RESTORE, milestone: action.milestone});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.MILESTONE_RESTORE_FAILURE, milestone: action.milestone, status: 500, err});

		console.error('Error restoring from database', err);
	}
}